import React, { useEffect, useMemo, useState } from "react";
import ModalShell from "./ModalShell";
import Pager from "./Pager";
import "./SelectExpertsModal.css";

const PAGE_SIZE = 5;

function getExpertName(e) {
  return (
    e?.name ??
    e?.full_name ??
    e?.name_kz ??
    [e?.last_name, e?.first_name, e?.patronymic].filter(Boolean).join(" ") ??
    "—"
  );
}

const getExpertSubject = (e) =>
  e?.subject ?? e?.subject_kz ?? e?.subject_ru ?? e?.discipline ?? e?.science ?? "—";

const getExpertEmail = (e) =>
  e?.email ?? e?.user?.email ?? e?.email_kz ?? e?.email_ru ?? "—";

export default function SelectExpertsModal({
  t,
  locale = "kz",
  serviceTypeId = 1,
  scientists = [],
  teachers = [],
  maxScientists = 50,
  maxTeachers = 50,
  onViewExpert,
  onSaveDraft,
  draftLoading = false,  // ← добавить
  onClose,
}) {
  const isThematic = Number(serviceTypeId) === 2;

  const [mode, setMode] = useState(isThematic ? "auto" : "manual");

  const [selectedScientists, setSelectedScientists] = useState([]);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [primaryScientistId, setPrimaryScientistId] = useState(null);

  const [autoScientistCount, setAutoScientistCount] = useState(1);
  const [autoTeacherCount, setAutoTeacherCount] = useState(isThematic ? 2 : 0);

  useEffect(() => {
    if (isThematic) {
      setMode("auto");
      setAutoScientistCount(1);
      setAutoTeacherCount(2);
    }
  }, [isThematic]);

  const [expertSearch, setExpertSearch] = useState("");
  const [subjectSci, setSubjectSci] = useState("");
  const [subjectTea, setSubjectTea] = useState("");

  const scientistSubjects = useMemo(() => {
    const set = new Set(
      (scientists || [])
        .map(getExpertSubject)
        .filter((s) => s && s !== "—")
        .map((s) => String(s).trim())
    );
    return Array.from(set).sort((a, b) =>
      a.localeCompare(b, locale === "ru" ? "ru" : "kk")
    );
  }, [scientists, locale]);

  const teacherSubjects = useMemo(() => {
    const set = new Set(
      (teachers || [])
        .map(getExpertSubject)
        .filter((s) => s && s !== "—")
        .map((s) => String(s).trim())
    );
    return Array.from(set).sort((a, b) =>
      a.localeCompare(b, locale === "ru" ? "ru" : "kk")
    );
  }, [teachers, locale]);

  const filteredScientists = useMemo(() => {
    const subj = subjectSci.toLowerCase();
    return (scientists || []).filter((e) => {
      const s = (getExpertSubject(e) || "").toLowerCase();
      return !subj || s === subj;
    });
  }, [scientists, subjectSci]);

  const filteredTeachers = useMemo(() => {
    const q = expertSearch.toLowerCase();
    const subj = subjectTea.toLowerCase();

    return (teachers || [])
      .filter((x) => getExpertName(x).toLowerCase().includes(q))
      .filter((e) => {
        const s = (getExpertSubject(e) || "").toLowerCase();
        return !subj || s === subj;
      });
  }, [teachers, expertSearch, subjectTea]);

  const [scientistPage, setScientistPage] = useState(1);
  const [teacherPage, setTeacherPage] = useState(1);

  const scientistTotalPages = Math.max(1, Math.ceil(filteredScientists.length / PAGE_SIZE));
  const teacherTotalPages = Math.max(1, Math.ceil(filteredTeachers.length / PAGE_SIZE));

  const pagedScientists = useMemo(
    () => filteredScientists.slice((scientistPage - 1) * PAGE_SIZE, scientistPage * PAGE_SIZE),
    [filteredScientists, scientistPage]
  );

  const pagedTeachers = useMemo(
    () => filteredTeachers.slice((teacherPage - 1) * PAGE_SIZE, teacherPage * PAGE_SIZE),
    [filteredTeachers, teacherPage]
  );

  useEffect(() => {
    if (primaryScientistId && !selectedScientists.includes(primaryScientistId)) {
      setPrimaryScientistId(null);
    }
  }, [selectedScientists, primaryScientistId]);

  useEffect(() => {
    if (selectedScientists.length === 1) {
      setPrimaryScientistId(selectedScientists[0]);
    }
  }, [selectedScientists]);

  const selectedExpertsCount = selectedScientists.length + selectedTeachers.length;

  const canSaveManual =
    selectedExpertsCount > 0 &&
    selectedScientists.length >= 1 &&
    (!!primaryScientistId || selectedScientists.length === 1);

const canSaveAuto =
  autoScientistCount !== "" &&
  autoTeacherCount !== "" &&
  Number(autoScientistCount) >= 1 &&
  Number(autoTeacherCount) >= 0;

  const canSave = mode === "manual" ? canSaveManual : canSaveAuto;

const footer = (
  <button
    className="sem-btn-primary"
    disabled={!canSave || draftLoading}
    onClick={() => {
      if (!canSave || draftLoading) return;

      if (mode === "manual") {
        onSaveDraft?.({
          selection_mode: "manual",
          expert_ids: [...selectedScientists, ...selectedTeachers],
          primary_scientist_id: primaryScientistId || null,
        });
        return;
      }

      if (isThematic) {
        onSaveDraft?.({
          scientist_count: Number(autoScientistCount),
          teacher_count: Number(autoTeacherCount),
        });
        return;
      }

      onSaveDraft?.({
        selection_mode: "auto",
        scientist_count: Number(autoScientistCount),
        teacher_count: Number(autoTeacherCount),
      });
    }}
  >
    {draftLoading
      ? (locale === "kz" ? "Сақталуда..." : "Сохранение...")
      : t.saveDraft}
  </button>
);

  const rowHandlers = (onToggle) => ({
    role: "button",
    tabIndex: 0,
    onClick: (e) => {
      if (e.target?.closest?.(".sem-btn-ghost")) return;
      if (e.target?.closest?.(".sem-radio-wrap")) return;
      onToggle();
    },
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onToggle();
      }
    },
  });

  return (
    <ModalShell wide title={t.selectExperts} onClose={onClose} footer={footer}>
      {!isThematic && (
        <section className="sem-block">
          <div className="sem-mode-switch">
            <button
              type="button"
              className={`sem-mode-btn ${mode === "manual" ? "is-active" : ""}`}
              onClick={() => setMode("manual")}
            >
              {locale === "kz" ? "Қолмен таңдау" : "Ручной выбор"}
            </button>

            <button
              type="button"
              className={`sem-mode-btn ${mode === "auto" ? "is-active" : ""}`}
              onClick={() => setMode("auto")}
            >
              {locale === "kz" ? "Автоматты таңдау" : "Автоматический выбор"}
            </button>
          </div>
        </section>
      )}

      {mode === "auto" && (
        <section className="sem-block">
          <h4 className="sem-title">
            {locale === "kz" ? "Автоматты таңдау" : "Автоматический выбор"}
          </h4>

          <div className="sem-auto-grid">
<label className="sem-auto-field">
  <span>{locale === "kz" ? "Ғалым саны" : "Количество ученых"}</span>
  <input
    type="number"
    min={1}
    value={autoScientistCount}
    onChange={(e) => {
      const raw = e.target.value;
      // разрешаем пустую строку, чтобы можно было стереть и напечатать заново
      setAutoScientistCount(raw === "" ? "" : Number(raw));
    }}
    onBlur={(e) => {
      // а вот когда ушли из поля — подчищаем: пусто/меньше 1 -> 1
      const n = Number(e.target.value);
      setAutoScientistCount(!e.target.value || Number.isNaN(n) || n < 1 ? 1 : n);
    }}
    className="sem-search"
  />
</label>

            <label className="sem-auto-field">
  <span>{locale === "kz" ? "Педагог саны" : "Количество педагогов"}</span>
  <input
    type="number"
    min={0}
    value={autoTeacherCount}
    onChange={(e) => {
      const raw = e.target.value;
      setAutoTeacherCount(raw === "" ? "" : Number(raw));
    }}
    onBlur={(e) => {
      const n = Number(e.target.value);
      setAutoTeacherCount(!e.target.value || Number.isNaN(n) || n < 0 ? 0 : n);
    }}
    className="sem-search"
  />
</label>
          </div>

          <div className="sem-hint">
            {isThematic
              ? locale === "kz"
                ? "Бұл топ тақырыптық жоспар бойынша келген. Сондықтан мұнда тек автоматты таңдау қолжетімді. Әдепкі мәндер: 1 ғалым, 2 педагог."
                : "Эта группа пришла по тематическому плану. Поэтому здесь доступен только автоматический выбор. Значения по умолчанию: 1 ученый, 2 педагога."
              : locale === "kz"
                ? "Система пән бойынша сарапшыларды кездейсоқ таңдайды. Егер бірнеше ғалым болса, негізгі ғалым автоматты түрде белгіленеді."
                : "Система случайно выберет экспертов по предмету. Если ученых несколько, основной ученый будет определен автоматически."}
          </div>
        </section>
      )}

      {mode === "manual" && (
        <>
          <section className="sem-block">
            <h4 className="sem-title">{locale === "kz" ? "Ғалымдар" : "Ученые"}</h4>

            <div className="sem-filters">
              <label className="sem-filter-label">
                {locale === "kz" ? "Пән бойынша сүзгі:" : "Фильтр по предмету:"}
                <select
                  className="sem-filter"
                  value={subjectSci}
                  onChange={(e) => {
                    setSubjectSci(e.target.value);
                    setScientistPage(1);
                  }}
                >
                  <option value="">
                    {locale === "kz" ? "Барлық пәндер" : "Все предметы"}
                  </option>
                  {scientistSubjects.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="sem-tablebox">
              <table className="sem-table sem-table--modern">
                <thead>
                  <tr>
                    <th>{locale === "kz" ? "Аты-жөні" : "ФИО"}</th>
                    <th>{locale === "kz" ? "Пән" : "Предмет"}</th>
                    <th>Email</th>
                    <th>{locale === "kz" ? "Негізгі" : "Основной"}</th>
                    <th aria-label="actions" />
                  </tr>
                </thead>
                <tbody>
                  {pagedScientists.length ? (
                    pagedScientists.map((expert) => {
                      const selected = selectedScientists.includes(expert.id);
                      const disabled =
                        !selected && selectedScientists.length >= maxScientists;

                      const toggle = () => {
                        if (selected) {
                          setSelectedScientists((prev) =>
                            prev.filter((id) => id !== expert.id)
                          );
                          return;
                        }

                        if (!disabled) {
                          setSelectedScientists((prev) => [...prev, expert.id]);
                        }
                      };

                      return (
                        <tr
                          key={`sci-${expert.id}`}
                          className={`sem-row ${selected ? "is-selected" : ""} ${disabled ? "is-disabled" : ""
                            }`}
                          {...rowHandlers(toggle)}
                        >
                          <td className="sem-col-name">
                            <span className="sem-chip sem-chip--name">
                              {getExpertName(expert)}
                            </span>
                          </td>
                          <td>
                            <span className="sem-chip">{getExpertSubject(expert)}</span>
                          </td>
                          <td>
                            <span className="sem-chip sem-chip--muted">
                              {getExpertEmail(expert)}
                            </span>
                          </td>
                          <td>
                            {selected ? (
                              <label
                                className="sem-radio-wrap"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="radio"
                                  name="primaryScientist"
                                  checked={primaryScientistId === expert.id}
                                  onChange={() => setPrimaryScientistId(expert.id)}
                                />
                                <span>{locale === "kz" ? "Негізгі" : "Основной"}</span>
                              </label>
                            ) : (
                              <span className="sem-muted">—</span>
                            )}
                          </td>
                          <td className="sem-col-actions">
                            <button
                              className="sem-btn-ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                onViewExpert?.(expert.id);
                              }}
                            >
                              👁 {locale === "kz" ? "Көру" : "Просмотр"}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={5} className="sem-empty">
                        {t.noExpertsFound}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="sem-counter">
              {t.selectedScientists}: {selectedScientists.length} / {maxScientists}
            </div>

            <Pager
              page={scientistPage}
              total={scientistTotalPages}
              onPage={setScientistPage}
            />
          </section>

          <section className="sem-block">
            <h4 className="sem-title">{locale === "kz" ? "Педагогтар" : "Педагоги"}</h4>

            <div className="sem-filters">
              <input
                className="sem-search"
                type="text"
                placeholder={locale === "kz" ? "Аты бойынша іздеу..." : "Поиск по имени..."}
                value={expertSearch}
                onChange={(e) => {
                  setExpertSearch(e.target.value);
                  setTeacherPage(1);
                }}
              />

              <label className="sem-filter-label">
                {locale === "kz" ? "Пән бойынша сүзгі:" : "Фильтр по предмету:"}
                <select
                  className="sem-filter"
                  value={subjectTea}
                  onChange={(e) => {
                    setSubjectTea(e.target.value);
                    setTeacherPage(1);
                  }}
                >
                  <option value="">
                    {locale === "kz" ? "Барлық пәндер" : "Все предметы"}
                  </option>
                  {teacherSubjects.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="sem-tablebox">
              <table className="sem-table sem-table--modern">
                <thead>
                  <tr>
                    <th>{locale === "kz" ? "Аты-жөні" : "ФИО"}</th>
                    <th>{locale === "kz" ? "Пән" : "Предмет"}</th>
                    <th>Email</th>
                    <th aria-label="actions" />
                  </tr>
                </thead>
                <tbody>
                  {pagedTeachers.length ? (
                    pagedTeachers.map((expert) => {
                      const selected = selectedTeachers.includes(expert.id);
                      const disabled =
                        !selected && selectedTeachers.length >= maxTeachers;

                      const toggle = () => {
                        if (selected) {
                          setSelectedTeachers((prev) =>
                            prev.filter((id) => id !== expert.id)
                          );
                        } else if (!disabled) {
                          setSelectedTeachers((prev) => [...prev, expert.id]);
                        }
                      };

                      return (
                        <tr
                          key={`tea-${expert.id}`}
                          className={`sem-row ${selected ? "is-selected" : ""} ${disabled ? "is-disabled" : ""
                            }`}
                          {...rowHandlers(toggle)}
                        >
                          <td className="sem-col-name">
                            <span className="sem-chip sem-chip--name">
                              {getExpertName(expert)}
                            </span>
                          </td>
                          <td>
                            <span className="sem-chip">{getExpertSubject(expert)}</span>
                          </td>
                          <td>
                            <span className="sem-chip sem-chip--muted">
                              {getExpertEmail(expert)}
                            </span>
                          </td>
                          <td className="sem-col-actions">
                            <button
                              className="sem-btn-ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                onViewExpert?.(expert.id);
                              }}
                            >
                              {locale === "kz" ? "Көру" : "Просмотр"}
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={4} className="sem-empty">
                        {t.noExpertsFound}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="sem-counter">
              {t.selectedTeachers}: {selectedTeachers.length} / {maxTeachers}
            </div>

            <Pager
              page={teacherPage}
              total={teacherTotalPages}
              onPage={setTeacherPage}
            />
          </section>
        </>
      )}
    </ModalShell>
  );
}